import React, { useState } from 'react';
import { Card, Table, Button, Modal, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetPayrollActivitiesAll,
  usePostPayrollActivities,
  usePutPayrollActivitiesActivityId,
  useDeletePayrollActivitiesActivityId,
  useGetPayrollActivitiesActivityIdRates,
  usePostPayrollActivitiesActivityIdRates,
  usePutPayrollActivitiesActivityIdRatesRateId,
  useDeletePayrollActivitiesActivityIdRatesRateId,
  getGetPayrollActivitiesAllQueryKey,
  getGetPayrollActivitiesActivityIdRatesQueryKey,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import {
  PayrollActivity,
  ActivityCategory,
  ActivityDefaultRate,
} from '../../api/generated/model';

const categoryLabels: Record<string, string> = {
  billable: 'Billable',
  non_billable: 'Non-Billable',
  pto: 'PTO',
};

const categoryVariants: Record<string, string> = {
  billable: 'success',
  non_billable: 'secondary',
  pto: 'info',
};

// Sub-component: expanded default rate history for an activity
const ActivityRatesSection: React.FC<{ activityId: string }> = ({ activityId }) => {
  const queryClient = useQueryClient();
  const { data: ratesData, isLoading } = useGetPayrollActivitiesActivityIdRates(activityId);
  const createMutation = usePostPayrollActivitiesActivityIdRates();
  const updateMutation = usePutPayrollActivitiesActivityIdRatesRateId();
  const deleteMutation = useDeletePayrollActivitiesActivityIdRatesRateId();

  const [showModal, setShowModal] = useState(false);
  const [editingRate, setEditingRate] = useState<ActivityDefaultRate | null>(null);
  const [formData, setFormData] = useState({ hourly_rate: '', start_date: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const rates = ratesData?.data || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetPayrollActivitiesActivityIdRatesQueryKey(activityId) });
    queryClient.invalidateQueries({ queryKey: getGetPayrollActivitiesAllQueryKey() });
  };

  const handleAdd = () => {
    setEditingRate(null);
    setFormData({ hourly_rate: '', start_date: new Date().toLocaleDateString('en-CA') });
    setError(null);
    setShowModal(true);
  };

  const handleEdit = (rate: ActivityDefaultRate) => {
    setEditingRate(rate);
    setFormData({
      hourly_rate: String(rate.hourly_rate ?? ''),
      start_date: rate.start_date || '',
    });
    setError(null);
    setShowModal(true);
  };

  const handleDelete = async (rate: ActivityDefaultRate) => {
    if (!rate.id) return;
    if (!window.confirm('Delete this rate entry?')) return;
    try {
      await deleteMutation.mutateAsync({ activityId, rateId: rate.id });
      invalidate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete rate');
    }
  };

  const handleSubmit = async () => {
    if (!formData.hourly_rate) { setError('Hourly rate is required'); return; }
    if (!formData.start_date) { setError('Start date is required'); return; }
    setSaving(true);
    setError(null);
    try {
      if (editingRate?.id) {
        await updateMutation.mutateAsync({
          activityId,
          rateId: editingRate.id,
          data: {
            hourly_rate: parseFloat(formData.hourly_rate),
            start_date: formData.start_date,
          },
        });
      } else {
        await createMutation.mutateAsync({
          activityId,
          data: {
            hourly_rate: parseFloat(formData.hourly_rate),
            start_date: formData.start_date,
          },
        });
      }
      invalidate();
      setShowModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <tr>
        <td colSpan={6} className="bg-body-tertiary">
          <div className="text-center py-2">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading rate history...
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr>
        <td colSpan={6} className="p-0">
          <div className="bg-body-tertiary px-4 py-3">
            {error && !showModal && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-2">
                {error}
              </Alert>
            )}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="small">Default Rate History</strong>
              <Button variant="outline-primary" size="sm" onClick={handleAdd}>
                <i className="bi bi-plus-lg me-1"></i>Add Rate
              </Button>
            </div>
            <Table size="sm" className="mb-0 bg-body">
              <thead>
                <tr>
                  <th>Hourly Rate</th>
                  <th>Start Date</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-2">
                      No rate history. The activity's default rate will be used.
                    </td>
                  </tr>
                ) : (
                  rates.map((rate) => (
                    <tr key={rate.id}>
                      <td className="fw-semibold">${(rate.hourly_rate ?? 0).toFixed(2)}/hr</td>
                      <td>{rate.start_date}</td>
                      <td>
                        <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(rate)}>
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(rate)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            <small className="text-muted mt-2 d-block">
              Rate changes only affect new timesheet entries. Approved timesheets retain their original rates.
            </small>
          </div>
        </td>
      </tr>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingRate ? 'Edit Default Rate' : 'Add Default Rate'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <Form.Group className="mb-3">
              <Form.Label>Hourly Rate ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={formData.hourly_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
              />
              <Form.Text className="text-muted">
                This rate applies to timesheet entries on or after this date.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <><Spinner animation="border" size="sm" className="me-1" />Saving...</>
            ) : (
              editingRate ? 'Update' : 'Create'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Main component
const PayrollActivitiesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: activitiesData, isLoading } = useGetPayrollActivitiesAll();
  const createMutation = usePostPayrollActivities();
  const updateMutation = usePutPayrollActivitiesActivityId();
  const deleteMutation = useDeletePayrollActivitiesActivityId();

  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<PayrollActivity | null>(null);
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    category: ActivityCategory;
    active: boolean;
  }>({
    name: '',
    category: ActivityCategory.ActivityCategoryBillable,
    active: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const activities = activitiesData?.data || [];

  const resetForm = () => {
    setFormData({
      name: '',
      category: ActivityCategory.ActivityCategoryBillable,
      active: true,
    });
    setEditingActivity(null);
    setError(null);
  };

  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (activity: PayrollActivity) => {
    setEditingActivity(activity);
    setFormData({
      name: activity.name || '',
      category: (activity.category as ActivityCategory) || ActivityCategory.ActivityCategoryBillable,
      active: activity.active ?? true,
    });
    setError(null);
    setShowModal(true);
  };

  const handleDelete = async (activity: PayrollActivity) => {
    if (!activity.id) return;
    if (!window.confirm(`Delete activity "${activity.name}"? This cannot be undone.`)) return;
    try {
      await deleteMutation.mutateAsync({ activityId: activity.id });
      queryClient.invalidateQueries({ queryKey: getGetPayrollActivitiesAllQueryKey() });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete activity');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingActivity?.id) {
        // Edit: only name, category, active (rates managed via history)
        await updateMutation.mutateAsync({
          activityId: editingActivity.id,
          data: {
            name: formData.name,
            category: formData.category,
            active: formData.active,
          },
        });
      } else {
        await createMutation.mutateAsync({
          data: {
            name: formData.name,
            category: formData.category,
          },
        });
      }
      queryClient.invalidateQueries({ queryKey: getGetPayrollActivitiesAllQueryKey() });
      setShowModal(false);
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpanded = (activityId: string) => {
    setExpandedActivityId(prev => prev === activityId ? null : activityId);
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading activities...
      </div>
    );
  }

  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Payroll Activities</h5>
            <Button variant="primary" size="sm" onClick={handleAdd}>
              <i className="bi bi-plus-lg me-1"></i>Add Activity
            </Button>
          </div>

          {error && !showModal && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Table hover responsive>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Name</th>
                <th>Category</th>
                <th>Current Rate</th>
                <th>Status</th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activities.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No activities configured. Click "Add Activity" to create one.
                  </td>
                </tr>
              ) : (
                activities.map((activity) => {
                  const isExpanded = expandedActivityId === activity.id;
                  return (
                    <React.Fragment key={activity.id}>
                      <tr
                        style={{ cursor: 'pointer' }}
                        className={isExpanded ? 'table-active' : ''}
                      >
                        <td onClick={() => toggleExpanded(activity.id!)}>
                          <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                        </td>
                        <td onClick={() => toggleExpanded(activity.id!)}>{activity.name}</td>
                        <td onClick={() => toggleExpanded(activity.id!)}>
                          <Badge bg={categoryVariants[activity.category || 'billable']}>
                            {categoryLabels[activity.category || 'billable']}
                          </Badge>
                        </td>
                        <td onClick={() => toggleExpanded(activity.id!)}>
                          {(() => {
                            if (activity.rates?.length) {
                              const rate = activity.rates[activity.rates.length - 1];
                              return `$${(rate.hourly_rate ?? 0).toFixed(2)}/hr`;
                            }
                            return '-';
                          })()}
                        </td>
                        <td onClick={() => toggleExpanded(activity.id!)}>
                          <Badge bg={activity.active ? 'success' : 'secondary'}>
                            {activity.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(activity)}>
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(activity)}>
                            <i className="bi bi-trash"></i>
                          </Button>
                        </td>
                      </tr>
                      {isExpanded && <ActivityRatesSection activityId={activity.id!} />}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingActivity ? 'Edit Activity' : 'Add Activity'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Software Development"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as ActivityCategory }))}
              >
                <option value={ActivityCategory.ActivityCategoryBillable}>Billable</option>
                <option value={ActivityCategory.ActivityCategoryNonBillable}>Non-Billable</option>
                <option value={ActivityCategory.ActivityCategoryPto}>PTO</option>
              </Form.Select>
            </Form.Group>

            {editingActivity && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Active"
                  checked={formData.active}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <><Spinner animation="border" size="sm" className="me-1" />Saving...</>
            ) : (
              editingActivity ? 'Update' : 'Create'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PayrollActivitiesTab;
